import { useTranslation } from "react-i18next";
import { TableColumn } from "react-data-table-component";
import React, { ChangeEvent, useEffect, useMemo, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Spinner,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
  Input,
  Label,
} from "reactstrap";
import { useHistory } from "react-router-dom";
import { DebounceInput } from "react-debounce-input";
import moment from "moment";
import { toast, ToastContainer } from "react-toastify";

import {
  ColumnHeader,
  TextColumn,
  DateColumn,
  AppTable,
  TableSpinner,
} from "../../../components";
import { BreadCrumb } from "../../../components/Common";
import { TransactionHistoryV2Dto } from "../../../interfaces";
import { EducationalStageDto } from "../../../interfaces/curriculum";
import { PaginateQuery } from "../../../models";
import {
  useGetTransactionHistoryV2Query,
  useVerifyTransactionMutation,
  useToggleRefundStatusMutation,
} from "../../../store/api/transactionApi";
import Section from "../../Dashboard/Section";
import { PaymentStatusCopiedEnum, PaymentThroughEnum } from "../../../enums";
import ConfirmationModal from "../../../components/Common/ConfirmationModal";
import { useGetEducationSystemSelectListQuery } from "../../../store/api/educationSystemApi";
import { useGetCurriculumSelectListQuery } from "../../../store/api/curriculumApi";
import { useGetCourseSelectListQuery } from "../../../store/api/courseApi";
import { useGetEducationalStagesQuery } from "../../../store/api/educationalStageApi";
// @ts-ignore
import "../../Users/CPUsers/TeacherAdmins.scss";

const labelOf = (
  item:
    | {
        value?: { en?: string; ar?: string };
        name?: { en?: string; ar?: string };
      }
    | undefined,
  langKey: "en" | "ar",
) =>
  item?.value?.[langKey] ||
  item?.value?.en ||
  item?.name?.[langKey] ||
  item?.name?.en ||
  "";

const DATE_FORMAT = "YYYY-MM-DD";

const defaultDateRange = () => {
  const today = moment().format(DATE_FORMAT);
  return { from: today, to: today };
};

const toDateRangeValue = (from: string, to: string): Date[] => [
  moment(from, DATE_FORMAT).toDate(),
  moment(to, DATE_FORMAT).toDate(),
];

type TProps = {};
export const TransactionHistoryV2Page: React.FC<TProps> = () => {
  //#region hooks

  const history = useHistory();
  const { t, i18n } = useTranslation();
  const langKey = i18n.language === "ar" ? "ar" : "en";
  //#endregion

  //#region internal state

  const initState = new PaginateQuery(10, "transaction_date:DESC", {
    ...defaultDateRange(),
    ["status"]: "",
  });
  const [paginateQuery, setPaginateQuery] = useState<PaginateQuery>(initState);
  const [queryReady, setQueryReady] = useState(false);
  const [filterRefund, setFilterRefund] = useState<string>("");
  const [filterVerificationStatus, setFilterVerificationStatus] =
    useState<string>("");
  const [filterDepositStatus, setFilterDepositStatus] = useState<string>("");
  const [filterEducationSystemId, setFilterEducationSystemId] =
    useState<string>("");
  const [filterCurriculumId, setFilterCurriculumId] = useState<string>("");
  const [filterEducationalStageId, setFilterEducationalStageId] =
    useState<string>("");
  const [filterCourseId, setFilterCourseId] = useState<string>("");
  const [filterGradeId, setFilterGradeId] = useState<string>("");
  const [filterGenderId, setFilterGenderId] = useState<string>("");
  const [filterStatusId, setFilterStatusId] = useState<string>("");
  const [filterMethodId, setFilterMethodId] = useState<string>("");
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] =
    useState<TransactionHistoryV2Dto | null>();
  const [actionType, setActionType] = useState<string>();
  const [actionTitle, setActionTitle] = useState<string>("");
  const [actionDescription, setActionDescription] = useState<string>("");
  const [dateValue, setDateValue] = useState<Date[]>(() => {
    const { from, to } = defaultDateRange();
    return toDateRangeValue(from, to);
  });
  const [verifyingId, setVerifyingId] = useState<string | null>();
  //#endregion

  //#region RTK Query hooks
  const { data: educationSystems } = useGetEducationSystemSelectListQuery();
  const { data: curricula } = useGetCurriculumSelectListQuery(
    filterEducationSystemId || undefined,
    { skip: !filterEducationSystemId },
  );
  const { data: courses } = useGetCourseSelectListQuery();

  const stagesQuery = useMemo(() => {
    const q = new PaginateQuery();
    q.limit = 1000;
    q.page = 1;
    q.sortBy = "sequence_number:ASC";
    q.filter = {
      curriculumId: filterCurriculumId,
      courseId: filterCourseId,
    };
    return q;
  }, [filterCurriculumId, filterCourseId]);

  const { data: stagesResponse } = useGetEducationalStagesQuery(stagesQuery, {
    skip: !filterCurriculumId || !filterCourseId,
  });
  const stages: EducationalStageDto[] = stagesResponse?.data || [];
  const selectedStage = useMemo(
    () => stages.find((s) => s.id === filterEducationalStageId),
    [stages, filterEducationalStageId],
  );
  const grades = selectedStage?.grades || [];

  const {
    data: response,
    isLoading: loading,
    refetch,
  } = useGetTransactionHistoryV2Query(paginateQuery, { skip: !queryReady });

  const [verifyTransaction, { isLoading: isLoadingVerify }] =
    useVerifyTransactionMutation();

  const [
    toggleRefundStatus,
    {
      isLoading: isLoadingToggleRefundStatus,
      error: errorToggleRefundStatusRtk,
    },
  ] = useToggleRefundStatusMutation();
  const errorToggleRefundStatus = errorToggleRefundStatusRtk
    ? (errorToggleRefundStatusRtk as any)?.data?.message || "An error occurred"
    : undefined;
  //#endregion

  document.title = `${t("documentTitle", { pageName: "transactionHistory" })}`;

  const columns: TableColumn<TransactionHistoryV2Dto>[] = [
    {
      name: <ColumnHeader title="date" />,
      cell: (row) => <DateColumn date={row.createdAt} />,
      sortable: true,
      minWidth: "160px",
    },
    {
      name: <ColumnHeader title="student" />,
      cell: (row) => (
        <React.Fragment>
          <div>
            <div>
              <strong className="text-capitalize">
                {row.user?.name[i18n.language === "ar" ? "ar" : "en"] || "n/a"}
              </strong>
            </div>
            <div>
              <a
                style={{ cursor: "pointer" }}
                href={`tel:+${row.user?.phone?.countryCode}${row.user?.phone?.phoneNumber}`}
              >
                <u dir="ltr">
                  {" "}
                  {`+${row.user?.phone?.countryCode}${row.user?.phone?.phoneNumber}`}
                </u>
              </a>
            </div>
            <div>
              <span>
                {row.subscription?.plan?.course?.name
                  ? row.subscription?.plan?.course?.name[
                      i18n.language === "ar" ? "ar" : "en"
                    ]
                  : "n/a"}
              </span>
            </div>
            <div>
              {row.subscription?.plan?.grade?.name
                ? row.subscription?.plan?.grade?.name[
                    i18n.language === "ar" ? "ar" : "en"
                  ]
                : "n/a"}
            </div>
          </div>
        </React.Fragment>
      ),
    },

    {
      name: <ColumnHeader title="plan" />,
      cell: (row) => (
        <React.Fragment>
          <div>
            <div>
              <strong className="text-capitalize">
                {i18n.language === "ar"
                  ? row?.subscription?.plan?.name?.ar
                  : row?.subscription?.plan?.name?.en}
              </strong>
            </div>

            <div>
              <span>
                {i18n.language === "ar"
                  ? row?.subscription?.plan?.course?.name?.ar
                  : row?.subscription?.plan?.course?.name?.en}
              </span>
            </div>
            <div>
              {i18n.language === "ar"
                ? row?.subscription?.plan?.grade?.name?.ar
                : row?.subscription?.plan?.grade?.name?.en}
            </div>
            <div className="mt-1">
              <span
                className={`badge ${
                  row.subscription?.purchaseKind === "upgrade"
                    ? "bg-warning text-dark"
                    : row.subscription?.purchaseKind === "replacement"
                      ? "bg-secondary"
                      : "bg-success"
                }`}
              >
                {row.subscription?.purchaseKind === "upgrade"
                  ? t("purchaseUpgraded")
                  : row.subscription?.purchaseKind === "replacement"
                    ? t("purchaseReplacement")
                    : t("purchaseSubscribed")}
              </span>
            </div>
            {row.subscription?.purchaseKind === "upgrade" &&
              row.subscription.previousPlan?.name && (
                <div className="text-muted small">
                  {t("upgradedFrom", {
                    plan:
                      i18n.language === "ar"
                        ? row.subscription.previousPlan.name.ar
                        : row.subscription.previousPlan.name.en,
                  })}
                </div>
              )}
          </div>
        </React.Fragment>
      ),
    },
    {
      name: <ColumnHeader title="method" />,
      cell: (row) => <TextColumn text={row.paymentThrough || "-"} />,
      sortable: true,
    },
    {
      name: <ColumnHeader title="status" />,
      cell: (row) => (
        <div>
          <TextColumn text={row.status || "-"} />
          <TextColumn
            className={row.depositStatus == "Deposited" ? "" : "text-danger"}
            text={row.depositStatus || "-"}
          />
          <TextColumn
            className={row.isVerified ? "" : "text-danger"}
            text={row.isVerified ? "Verified" : "Unverified"}
          />
          <Button
            className="btn btn-success btn-sm"
            onClick={async () => {
              setVerifyingId(row.id);
              try {
                await verifyTransaction(row.id).unwrap();
              } catch (err: any) {
                toast.error(err?.data?.message || "Verification failed", {
                  autoClose: 3000,
                });
              }
              setVerifyingId(null);
            }}
          >
            {row.id === verifyingId && isLoadingVerify ? (
              <Spinner size={"sm"} />
            ) : (
              t("verify")
            )}
          </Button>
        </div>
      ),
      sortable: true,
      minWidth: "160px",
    },
    {
      name: <ColumnHeader title="refundStatus" />,
      cell: (row) => (
        <TextColumn text={row.isRefunded ? t("refunded") : t("-")} />
      ),
      sortable: true,
    },
    {
      name: <ColumnHeader title="invoice" />,
      cell: (row) => (
        <TextColumn
          text={
            row.invoiceId !== undefined && row.invoiceId !== null
              ? row.invoiceId + ""
              : "-"
          }
        />
      ),
      sortable: true,
    },
    {
      name: <ColumnHeader title="reference" />,
      cell: (row) => (
        <TextColumn
          text={
            row.invoiceReference !== undefined && row.invoiceReference !== null
              ? row.invoiceReference + ""
              : "-"
          }
        />
      ),
      sortable: true,
    },
    {
      name: <ColumnHeader title="amount" />,
      cell: (row) => (
        <TextColumn
          text={
            row.amount != 0 && row.amount !== undefined && row.amount !== null
              ? row.amount + ""
              : "-"
          }
        />
      ),
      sortable: true,
    },
    {
      name: <ColumnHeader title="dueDeposit" />,
      cell: (row) => (
        <TextColumn
          text={
            row.dueDeposit != 0 &&
            row.dueDeposit !== undefined &&
            row.dueDeposit !== null
              ? row.dueDeposit + ""
              : "-"
          }
        />
      ),
      sortable: true,
    },
    {
      name: <ColumnHeader title="currency" />,
      cell: (row) => <TextColumn text={row.currency || "-"} />,
      sortable: false,
    },
    {
      name: <ColumnHeader title="action" />,
      sortable: false,
      width: "100px",
      cell: (row) => (
        <UncontrolledDropdown>
          <DropdownToggle tag="a" id="dropdownMenuLink2" role="button">
            <i className="ri-more-fill fs-17 dropdown-menu-button-custom"></i>
          </DropdownToggle>
          <DropdownMenu>
            {!row.isRefunded && (
              <DropdownItem
                style={{
                  alignSelf: "flex-start",
                  display: "flex",
                  alignItems: "center",
                }}
                className="dropdown-item edit-list"
                onClick={() => onAction(row, "refund")}
              >
                {row.isRefunded ? t("undoMarkRefund") : t("markRefund")}
              </DropdownItem>
            )}
          </DropdownMenu>
        </UncontrolledDropdown>
      ),
    },
  ];

  const onAction = (_row: TransactionHistoryV2Dto, _actionType: "refund") => {
    setSelectedTransaction(_row);
    setActionType(_actionType);
    const previousPlanName =
      i18n.language === "ar"
        ? _row.subscription?.previousPlan?.name?.ar
        : _row.subscription?.previousPlan?.name?.en;
    const upgradeNote =
      _row.subscription?.purchaseKind === "upgrade"
        ? ` ${t(
            _row.isRefunded
              ? "undoRefundUpgradeSupersedes"
              : "refundUpgradeRestoresPrevious",
            { plan: previousPlanName || "-" },
          )}`
        : "";
    if (_actionType === "refund" && !_row.isRefunded) {
      setActionTitle(t("markRefundConfirmationTitle"));
      setActionDescription(
        t("markRefundConfirmationDescription", {
          invoice_id: _row.invoiceId,
          invoice_reference: _row.invoiceReference,
        }) + upgradeNote,
      );
    }
    if (_actionType === "refund" && _row.isRefunded) {
      setActionTitle(t("undoMarkRefundConfirmationTitle"));
      setActionDescription(
        t("undoMarkRefundConfirmationDescription", {
          invoice_id: _row.invoiceId || "-",
          invoice_reference: _row.invoiceReference || "-",
        }) + upgradeNote,
      );
    }
    setShowConfirmationModal(true);
  };

  const getSortByColumnName = (name: string) => {
    let sortBy;
    switch (name) {
      case "transaction_date":
        sortBy = 1;
        break;
      case "payment_through":
        sortBy = 4;
        break;
      case "status":
        sortBy = 5;
        break;
      case "is_refunded":
        sortBy = 6;
        break;
      case "deposit_status":
        sortBy = 7;
        break;
      case "invoice_id":
        sortBy = 8;
        break;
      case "invoice_reference":
        sortBy = 9;
        break;
      case "amount":
        sortBy = 10;
        break;
      case "due_deposit":
        sortBy = 11;
        break;
      default:
        sortBy = 1;
        break;
    }
    return sortBy;
  };

  const [defaultSortFieldId, setDefaultSortFieldId] = useState(
    getSortByColumnName("transaction_date:DESC"),
  );
  const [defaultSortAsc, setDefaultSortAsc] = useState(false);
  const [defaultPage, setDefaultPage] = useState(1);

  //#region effects

  useEffect(() => {
    const queryParams = new URLSearchParams(history.location.search);
    let filters: any = {};
    if (queryParams.has("filter[educationSystemId]")) {
      filters = {
        ...filters,
        ["educationSystemId"]:
          queryParams.get("filter[educationSystemId]") || "",
      };
      setFilterEducationSystemId(filters["educationSystemId"] as string);
    }
    if (queryParams.has("filter[curriculumId]")) {
      filters = {
        ...filters,
        ["curriculumId"]: queryParams.get("filter[curriculumId]") || "",
      };
      setFilterCurriculumId(filters["curriculumId"] as string);
    }
    if (queryParams.has("filter[educationalStageId]")) {
      filters = {
        ...filters,
        ["educationalStageId"]:
          queryParams.get("filter[educationalStageId]") || "",
      };
      setFilterEducationalStageId(filters["educationalStageId"] as string);
    }
    if (queryParams.has("filter[course]")) {
      filters = {
        ...filters,
        ["course"]: queryParams.get("filter[course]") || "",
      };
      setFilterCourseId(filters["course"] as string);
    }
    if (queryParams.has("filter[grade]")) {
      filters = {
        ...filters,
        ["grade"]: queryParams.get("filter[grade]") || "",
      };
      setFilterGradeId(filters["grade"] as string);
    }
    if (queryParams.has("filter[gender]")) {
      filters = {
        ...filters,
        ["gender"]: queryParams.get("filter[gender]") || "",
      };
      setFilterGenderId(filters["gender"] as string);
    }
    if (queryParams.has("filter[status]")) {
      filters = {
        ...filters,
        ["status"]: queryParams.get("filter[status]") || "",
      };
      setFilterStatusId(filters["status"] as string);
    }
    if (queryParams.has("filter[method]")) {
      filters = {
        ...filters,
        ["method"]: queryParams.get("filter[method]") || "",
      };
      setFilterMethodId(filters["method"] as string);
    }
    if (queryParams.has("filter[refunded]")) {
      filters = {
        ...filters,
        ["refunded"]: queryParams.get("filter[refunded]") || "",
      };
      setFilterRefund(filters["refunded"] as string);
    }
    if (queryParams.has("filter[is_verified]")) {
      filters = {
        ...filters,
        ["is_verified"]: queryParams.get("filter[is_verified]") || "",
      };
      setFilterVerificationStatus(filters["is_verified"] as string);
    }

    if (queryParams.has("filter[deposit_status]")) {
      filters = {
        ...filters,
        ["deposit_status"]: queryParams.get("filter[deposit_status]") || "",
      };
      setFilterDepositStatus(filters["deposit_status"] as string);
    }
    const { from, to } = defaultDateRange();
    const mergedFilter = {
      ...initState.filter,
      ...filters,
      from,
      to,
    };

    setDateValue(toDateRangeValue(from, to));

    let query: PaginateQuery = {
      limit: queryParams.has("limit")
        ? Number(queryParams.get("limit"))
        : paginateQuery.limit,
      page: queryParams.has("page")
        ? Number(queryParams.get("page"))
        : paginateQuery.page,
      sortBy: queryParams.has("sortBy")
        ? (queryParams.get("sortBy") as string)
        : paginateQuery.sortBy,
      search: queryParams.has("search")
        ? (queryParams.get("search") as string)
        : paginateQuery.search,
      filter: mergedFilter,
    };
    if (
      queryParams.get("sortBy") &&
      queryParams.get("sortBy")?.split(":").length &&
      queryParams.get("sortBy")?.split(":").length == 2
    ) {
      setDefaultSortAsc(
        (queryParams.get("sortBy")?.split(":")[1] as string) === "ASC",
      );
      setDefaultSortFieldId(
        getSortByColumnName(queryParams.get("sortBy")?.split(":")[0] as string),
      );
    } else {
      setDefaultSortAsc(false);
      setDefaultSortFieldId(2);
    }
    setDefaultPage(query.page);
    setPaginateQuery(query);
    setQueryReady(true);
  }, []);

  useEffect(() => {
    if (!queryReady) return;

    const { limit, page, search, sortBy, filter } = paginateQuery;
    let query = `?page=${page || 1}`;
    if (limit) query += `&limit=${limit}`;
    if (sortBy) query += `&sortBy=${sortBy}`;
    if (search) query += `&search=${search}`;
    if (filter && Object.keys(filter).length > 0) {
      Object.keys(filter).forEach((key: string) => {
        if (filter[key]) query += `&filter[${key}]=${filter[key]}`;
      });
    }
    if (query !== history.location.search) {
      history.replace({ search: query });
    }
  }, [paginateQuery, queryReady]);

  useEffect(() => {
    if (!stages.length) {
      if (filterEducationalStageId) {
        setFilterEducationalStageId("");
      }
      return;
    }
    if (
      filterEducationalStageId &&
      stages.some((s) => s.id === filterEducationalStageId)
    ) {
      return;
    }
  }, [stages, filterEducationalStageId]);

  //#endregion

  //#region handlers

  const onSearch = (searchText: string) => {
    setPaginateQuery((prev) => ({ ...prev, search: searchText, page: 1 }));
  };

  const getSortByColumnId = (columnId: number, sortDirection: string) => {
    let sortBy;
    switch (columnId) {
      case 1:
        sortBy = "transaction_date";
        break;
      case 4:
        sortBy = "payment_through";
        break;
      case 5:
        sortBy = "status";
        break;
      case 6:
        sortBy = "is_refunded";
        break;
      case 7:
        sortBy = "deposit_status";
        break;
      case 8:
        sortBy = "invoice_id";
        break;
      case 9:
        sortBy = "invoice_reference";
        break;
      case 10:
        sortBy = "amount";
        break;
      case 11:
        sortBy = "due_deposit";
        break;
      default:
        sortBy = "transaction_date";
        break;
    }
    return `${sortBy}:${sortDirection.toUpperCase()}`;
  };

  const onChangePage = (page: number) => {
    setPaginateQuery((prev) => ({ ...prev, page }));
  };

  const onChangeRowsPerPage = (
    currentRowsPerPage: number,
    currentPage: number,
  ) => {
    setPaginateQuery((prev) => ({
      ...prev,
      limit: currentRowsPerPage,
      page: currentPage,
    }));
  };

  const onSort = (
    column: TableColumn<TransactionHistoryV2Dto>,
    sortDirection: string,
  ) => {
    const sortBy = getSortByColumnId(column.id as number, sortDirection);
    setPaginateQuery((prev) => ({
      ...prev,
      sortBy: sortBy as string,
    }));
  };

  const onChangeEducationSystemFilter = (value: string) => {
    setFilterEducationSystemId(value);
    setFilterCurriculumId("");
    setFilterEducationalStageId("");
    setFilterGradeId("");
    setPaginateQuery((prev) => ({
      ...prev,
      page: 1,
      filter: {
        ...prev.filter,
        ["educationSystemId"]: value,
        ["curriculumId"]: "",
        ["educationalStageId"]: "",
        ["grade"]: "",
      },
    }));
  };

  const onChangeCurriculumFilter = (value: string) => {
    setFilterCurriculumId(value);
    setFilterEducationalStageId("");
    setFilterGradeId("");
    setPaginateQuery((prev) => ({
      ...prev,
      page: 1,
      filter: {
        ...prev.filter,
        ["curriculumId"]: value,
        ["educationalStageId"]: "",
        ["grade"]: "",
      },
    }));
  };

  const onChangeCourseFilter = (value: string) => {
    setFilterCourseId(value);
    setFilterEducationalStageId("");
    setFilterGradeId("");
    setPaginateQuery((prev) => ({
      ...prev,
      page: 1,
      filter: {
        ...prev.filter,
        ["course"]: value,
        ["educationalStageId"]: "",
        ["grade"]: "",
      },
    }));
  };

  const onChangeEducationalStageFilter = (value: string) => {
    setFilterEducationalStageId(value);
    setFilterGradeId("");
    setPaginateQuery((prev) => ({
      ...prev,
      page: 1,
      filter: {
        ...prev.filter,
        ["educationalStageId"]: value,
        ["grade"]: "",
      },
    }));
  };

  const onChangeGradeFilter = (value: string) => {
    setFilterGradeId(value);
    setPaginateQuery((prev) => ({
      ...prev,
      page: 1,
      filter: {
        ...prev.filter,
        ["grade"]: value,
      },
    }));
  };

  const onChangeStatusFilter = (value: string) => {
    setFilterStatusId(value);
    setPaginateQuery((prev) => ({
      ...prev,
      page: 1,
      filter: {
        ...prev.filter,
        ["status"]: value,
      },
    }));
  };

  const onChangeMethodFilter = (value: string) => {
    setFilterMethodId(value);
    setPaginateQuery((prev) => ({
      ...prev,
      page: 1,
      filter: {
        ...prev.filter,
        ["method"]: value,
      },
    }));
  };

  const onChangeRefundFilter = (value: any) => {
    setFilterRefund(value);
    setPaginateQuery((prev) => ({
      ...prev,
      page: 1,
      filter: {
        ...prev.filter,
        ["refunded"]: value,
      },
    }));
  };

  const onChangeFilterVerificationStatus = (value: any) => {
    setFilterVerificationStatus(value);
    setPaginateQuery((prev) => ({
      ...prev,
      page: 1,
      filter: {
        ...prev.filter,
        ["is_verified"]: value,
      },
    }));
  };

  const onChangeFilterDepositStatus = (value: any) => {
    setFilterDepositStatus(value);
    setPaginateQuery((prev) => ({
      ...prev,
      page: 1,
      filter: {
        ...prev.filter,
        ["deposit_status"]: value,
      },
    }));
  };

  const onClearFilters = () => {
    const { from, to } = defaultDateRange();
    setFilterRefund("");
    setFilterVerificationStatus("");
    setFilterDepositStatus("");
    setFilterEducationSystemId("");
    setFilterCurriculumId("");
    setFilterEducationalStageId("");
    setFilterCourseId("");
    setFilterGradeId("");
    setFilterGenderId("");
    setFilterStatusId("");
    setFilterMethodId("");
    setDateValue(toDateRangeValue(from, to));
    setPaginateQuery((prev) => ({
      ...prev,
      page: 1,
      search: "",
      filter: {
        from,
        to,
        ["governorate"]: "",
        ["city"]: "",
        ["school"]: "",
        ["educationSystemId"]: "",
        ["curriculumId"]: "",
        ["educationalStageId"]: "",
        ["course"]: "",
        ["grade"]: "",
        ["gender"]: "",
        ["status"]: "",
        ["method"]: "",
        ["refunded"]: "",
        ["is_verified"]: "",
        ["deposit_status"]: "",
      },
    }));
  };

  const onChangeDate = (dates: any) => {
    if (dates && dates.length === 2) {
      setDateValue(dates);
      setPaginateQuery((prev) => ({
        ...prev,
        page: 1,
        filter: {
          ...prev.filter,
          ["from"]: moment(new Date(dates[0])).format(DATE_FORMAT),
          ["to"]: moment(new Date(dates[1])).format(DATE_FORMAT),
        },
      }));
    } else {
      setDateValue([]);
      setPaginateQuery((prev) => ({
        ...prev,
        page: 1,
        filter: {
          ...prev.filter,
          ["from"]: "",
          ["to"]: "",
        },
      }));
    }
  };

  const onActionConfirm = async () => {
    if (actionType) {
      try {
        await toggleRefundStatus(selectedTransaction?.id as string).unwrap();
        setShowConfirmationModal(false);
      } catch (err: any) {
        toast.error(err?.data?.message || "An error occurred", {
          autoClose: 3000,
        });
      }
    }
    setSelectedTransaction(null);
  };
  //#endregion

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title={t("transactionHistory")}
            pageTitle={t("payment")}
          />
          <Row>
            <Col>
              <div className="h-100">
                <Section
                  value={dateValue}
                  onChange={(value: any) => onChangeDate(value)}
                />
              </div>
            </Col>
          </Row>
          <Row>
            <Col xxl={12}>
              <Card>
                <CardBody>
                  <Row className="g-2 mb-3">
                    <Col sm={4} className="mb-2">
                      <div className="search-box">
                        <DebounceInput
                          className="form-control"
                          placeholder={`${t("searchPlaceholder")}...`}
                          minLength={2}
                          debounceTimeout={300}
                          onChange={(
                            event: React.ChangeEvent<HTMLInputElement>,
                          ) => onSearch(event.target.value)}
                          value={paginateQuery.search}
                        />
                        <i className="ri-search-line search-icon"></i>
                      </div>
                    </Col>
                  </Row>

                  <div className="admin-teachers-filters border rounded p-3 mb-3 bg-light bg-opacity-50">
                    <Row className="g-2 align-items-end">
                      <Col md={2}>
                        <Label className="form-label">
                          {t("educationSystem")}
                        </Label>
                        <Input
                          type="select"
                          value={filterEducationSystemId}
                          className="form-select"
                          aria-label={t("filterByEducationSystem")}
                          onChange={(e) =>
                            onChangeEducationSystemFilter(e.target.value)
                          }
                        >
                          <option value="">{t("all")}</option>
                          {(educationSystems || []).map((s: any) => (
                            <option key={s.id} value={s.id}>
                              {labelOf(s, langKey)}
                            </option>
                          ))}
                        </Input>
                      </Col>
                      <Col md={2}>
                        <Label className="form-label">
                          {t("curriculumItem")}
                        </Label>
                        <Input
                          type="select"
                          value={filterCurriculumId}
                          className="form-select"
                          aria-label={t("filterByCurriculum")}
                          disabled={!filterEducationSystemId}
                          onChange={(e) =>
                            onChangeCurriculumFilter(e.target.value)
                          }
                        >
                          <option value="">{t("all")}</option>
                          {(curricula || []).map((c: any) => (
                            <option key={c.id} value={c.id}>
                              {labelOf(c, langKey)}
                            </option>
                          ))}
                        </Input>
                      </Col>
                      <Col md={2}>
                        <Label className="form-label">{t("course")}</Label>
                        <Input
                          type="select"
                          value={filterCourseId}
                          className="form-select"
                          aria-label={t("filterByCourse")}
                          onChange={(e) => onChangeCourseFilter(e.target.value)}
                        >
                          <option value="">{t("all")}</option>
                          {(courses || []).map((c: any) => (
                            <option key={c.id} value={c.id}>
                              {labelOf(c, langKey)}
                            </option>
                          ))}
                        </Input>
                      </Col>
                      <Col md={2}>
                        <Label className="form-label">
                          {t("educationalStage")}
                        </Label>
                        <Input
                          type="select"
                          value={filterEducationalStageId}
                          className="form-select"
                          aria-label={t("filterByEducationalStage")}
                          disabled={!filterCurriculumId || !filterCourseId}
                          onChange={(e) =>
                            onChangeEducationalStageFilter(e.target.value)
                          }
                        >
                          <option value="">{t("all")}</option>
                          {stages.map((stage) => (
                            <option key={stage.id} value={stage.id}>
                              {stage.name[langKey] || stage.name.en}
                            </option>
                          ))}
                        </Input>
                      </Col>
                      <Col md={2}>
                        <Label className="form-label">{t("grade")}</Label>
                        <Input
                          type="select"
                          value={filterGradeId}
                          className="form-select"
                          aria-label={t("filterByGrade")}
                          disabled={!filterEducationalStageId}
                          onChange={(e) => onChangeGradeFilter(e.target.value)}
                        >
                          <option value="">{t("all")}</option>
                          {grades.map((grade) => (
                            <option key={grade.id} value={grade.id}>
                              {grade.name[langKey] || grade.name.en}
                            </option>
                          ))}
                        </Input>
                      </Col>
                    </Row>
                  </div>

                  <Row className="g-2 mb-3">
                    <Col sm={4} className="mb-2">
                      <div className="search-box">
                        <select
                          value={filterStatusId}
                          className="form-select form-select-md"
                          onChange={(event: any) =>
                            onChangeStatusFilter(event.target.value)
                          }
                        >
                          <option value="">{t("filterByStatus")}</option>
                          {(
                            Object.keys(PaymentStatusCopiedEnum) as Array<
                              keyof typeof PaymentStatusCopiedEnum
                            >
                          ).map((key) => (
                            <option value={key}>
                              {PaymentStatusCopiedEnum[key]}
                            </option>
                          ))}
                        </select>
                      </div>
                    </Col>

                    <Col sm={4} className="mb-2">
                      <div className="search-box">
                        <select
                          value={filterRefund}
                          className="form-select form-select-md"
                          onChange={(event: any) =>
                            onChangeRefundFilter(event.target.value)
                          }
                        >
                          <option value="">{t("filterByRefundStatus")}</option>
                          <option value="1">{t("refunded")}</option>
                          <option value="0">{t("notRefunded")}</option>
                        </select>
                      </div>
                    </Col>
                    <Col sm={4} className="mb-2">
                      <div className="search-box">
                        <select
                          value={filterVerificationStatus}
                          className="form-select form-select-md"
                          onChange={(event: any) =>
                            onChangeFilterVerificationStatus(event.target.value)
                          }
                        >
                          <option value="">
                            {t("filterByVerificationStatus")}
                          </option>
                          <option value="1">{t("verified")}</option>
                          <option value="0">{t("unverified")}</option>
                        </select>
                      </div>
                    </Col>
                    <Col sm={4} className="mb-2">
                      <div className="search-box">
                        <select
                          value={filterDepositStatus}
                          className="form-select form-select-md"
                          onChange={(event: any) =>
                            onChangeFilterDepositStatus(event.target.value)
                          }
                        >
                          <option value="">{t("filterByDepositStatus")}</option>
                          <option value="Deposited">{t("deposited")}</option>
                          <option value="Not Deposited">
                            {t("notDeposited")}
                          </option>
                        </select>
                      </div>
                    </Col>
                    <Col sm={4} className="mb-2">
                      <div className="search-box">
                        <select
                          value={filterMethodId}
                          className="form-select form-select-md"
                          onChange={(event: any) =>
                            onChangeMethodFilter(event.target.value)
                          }
                        >
                          <option value="">{t("filterByPaymentMethod")}</option>
                          {(
                            Object.keys(PaymentThroughEnum) as Array<
                              keyof typeof PaymentThroughEnum
                            >
                          ).map((key) => (
                            <option value={key}>{key}</option>
                          ))}
                        </select>
                      </div>
                    </Col>
                  </Row>
                  <Row>
                    <Col className="col-sm-auto ms-auto">
                      <div className="list-grid-nav hstack gap-1">
                        <Button
                          color="success"
                          onClick={() => onClearFilters()}
                        >
                          {t("clearFilters")}
                        </Button>
                        <Button
                          color="success"
                          onClick={() => {
                            setPaginateQuery({ ...paginateQuery });
                            refetch();
                          }}
                        >
                          {t("refresh")}
                        </Button>
                      </div>
                    </Col>
                  </Row>
                  <Row>
                    <Col sm={12} className="mb-1">
                      <span className="h5">
                        <strong> {t("totalAmount")}</strong>:{" "}
                        {response?.data?.[0]?.totalAmount ?? 0}
                      </span>
                    </Col>
                    <Col sm={12} className="mb-3">
                      <span className="h5">
                        <strong> {t("totalDueDeposit")}</strong>:{" "}
                        {response?.data?.[0]?.totalDueDeposit?.toFixed(2) ?? 0}
                      </span>
                    </Col>
                    <Col sm={12} className="mb-1">
                      <span className="text-info">
                        <strong>{t("amount")}: </strong>
                        {t("theAmountCustomerPaid")}
                      </span>
                    </Col>
                    <Col sm={12}>
                      <span className="text-info">
                        <strong>{t("dueDeposit")}: </strong>
                        {t("theAmountYouWillReceive")}
                      </span>
                    </Col>
                  </Row>
                  <Row>
                    <Col sm={12}>
                      <AppTable
                        theme={"dark"}
                        columns={columns}
                        data={response?.data || []}
                        progressPending={loading}
                        progressComponent={<TableSpinner />}
                        pagination
                        paginationServer
                        sortServer
                        onSort={onSort}
                        onChangeRowsPerPage={onChangeRowsPerPage}
                        onChangePage={onChangePage}
                        defaultSortFieldId={defaultSortFieldId}
                        paginationDefaultPage={defaultPage}
                        defaultSortAsc={defaultSortAsc}
                        persistTableHead
                        paginationTotalRows={response?.meta?.totalItems}
                        paginationComponentOptions={{
                          rowsPerPageText: t("rowsPerPage"),
                          rangeSeparatorText: t("of"),
                        }}
                        fixedHeader={true}
                        fixedHeaderScrollHeight="60vh"
                      />
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
      <ConfirmationModal
        confirmationButtonInProgressTitle={t("saving")}
        confirmationButtonTitle={actionTitle}
        description={actionDescription}
        icon={<></>}
        title={t("areYouSure")}
        loading={isLoadingToggleRefundStatus}
        show={showConfirmationModal}
        onConfirmClick={() => onActionConfirm()}
        onCloseClick={() => {
          setShowConfirmationModal(false);
        }}
        error={errorToggleRefundStatus}
      />
    </React.Fragment>
  );
};
